/**
 * Wiederverwendbare UI-Bausteine — exakt im Stitch-Design ("Premium Hospitality",
 * Material-3-Token). Quelle: maestro_dashboard/code.html u. a. Stitch-Exporte.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { PILL_CLASSES, type StatusMeta } from "./format";

export function Icon({ name, className = "" }: { name: string; className?: string }) {
  return <span className={`material-symbols-outlined ${className}`}>{name}</span>;
}

/** Status-Pill nach Claude-Design-Entwurf: Label + fuehrender Farbpunkt (erbt die Ton-Farbe via currentColor). */
export function StatusPill({ meta }: { meta: StatusMeta | undefined }) {
  if (!meta) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${PILL_CLASSES[meta.tone]}`}
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />
      {meta.label}
    </span>
  );
}

/**
 * KPI-Kachel nach dem Design-Export: weiße Karte, warme Linie, weicher Schatten;
 * Icon-Chip (akzent-getönt) oben links, optionales Delta-Badge oben rechts, große
 * Zahl (26px) darüber das Label. Einheitlich für alle Kacheln (kein gefülltes Terracotta mehr).
 */
export function KpiCard({
  label,
  value,
  icon,
  delta,
  iconTone = "primary",
  to,
}: {
  label: string;
  value: ReactNode;
  icon?: string;
  delta?: { text: string; tone?: "up" | "warn" | "down" | "neutral" };
  /** H9: semantische Icon-Farbe statt immer Accent (z. B. Zahlungen-KPIs gruen/amber/blau). */
  iconTone?: "primary" | "success" | "warn" | "info";
  /** CX-Audit 2026-07-17: optionales Klickziel — eine KPI-Kachel ist eine Frage
   *  („Wie viele offene Anfragen?"), der Klick fuehrt zur Antwort-Liste statt in
   *  eine Sackgasse. Ohne `to` bleibt die Kachel reine Anzeige. */
  to?: string;
}) {
  const deltaTone = {
    up: "text-tertiary bg-[#E4F1E8] dark:bg-[#2E9963]/18 dark:text-[#7bc49a]",
    warn: "text-[#96650d] bg-[#faefd9] dark:bg-[#C98A1B]/20 dark:text-[#e0b366]",
    down: "text-error bg-[#F8E7E3] dark:bg-[#B23B32]/22 dark:text-[#e39b92]",
    neutral: "text-secondary bg-[#F0EAE0] dark:bg-white/[0.08] dark:text-[#b8ae9e]",
  }[delta?.tone ?? "neutral"];
  const iconToneCls = {
    primary: "bg-primary/10 text-primary dark:bg-primary/25 dark:text-[#f0b49b]",
    success: "bg-[#E4F1E8] text-[#217A50] dark:bg-[#2E9963]/18 dark:text-[#7bc49a]",
    warn: "bg-[#FAEFD9] text-[#96650D] dark:bg-[#C98A1B]/20 dark:text-[#e0b366]",
    info: "bg-[#E7EEF7] text-[#31619B] dark:bg-[#31619B]/20 dark:text-[#9dbbe0]",
  }[iconTone];
  const baseCls =
    "rounded-[14px] border border-surface-variant bg-surface-container-lowest p-4 shadow-[0_8px_18px_-10px_rgba(45,38,26,0.14)] dark:border-white/10 dark:bg-night-card dark:shadow-[0_8px_18px_-10px_rgba(0,0,0,0.5)]";
  const body = (
    <>
      <div className="flex items-center justify-between">
        {icon ? (
          <div className={`grid h-[30px] w-[30px] place-items-center rounded-[9px] ${iconToneCls}`}>
            <Icon name={icon} className="text-[18px]" />
          </div>
        ) : (
          <span />
        )}
        {delta && (
          <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${deltaTone}`}>{delta.text}</span>
        )}
      </div>
      <div className="mt-2.5">
        <div className="text-[26px] font-bold leading-none tracking-[-0.02em] text-on-background dark:text-inverse-on-surface">
          {value}
        </div>
        <div className="mt-1.5 text-[11.5px] font-medium text-secondary dark:text-secondary-fixed-dim">{label}</div>
      </div>
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        className={`${baseCls} block transition-shadow hover:shadow-[0_10px_22px_-10px_rgba(45,38,26,0.28)] dark:hover:shadow-[0_10px_22px_-10px_rgba(0,0,0,0.7)]`}
      >
        {body}
      </Link>
    );
  }
  return <div className={baseCls}>{body}</div>;
}

/** Rechts einfahrender Detail-Drawer mit Backdrop-Blur (Stitch: "Drawers slide in from the right").
 *  `width` uebersteuert die Standardbreite (max-w-lg = 512px), `subtitle` blendet eine Meta-Zeile
 *  unter dem Titel ein, `headerAccessory` platziert ein Element (z. B. Status-Pill) inline im Kopf. */
export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
  width,
  subtitle,
  headerAccessory,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
  subtitle?: ReactNode;
  headerAccessory?: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[3px]" onClick={onClose} aria-hidden />
      <aside
        className={`absolute inset-y-0 right-0 flex w-full flex-col bg-surface-container-lowest shadow-2xl dark:bg-night-card ${width ? "max-w-full" : "max-w-lg"}`}
        style={width ? { width } : undefined}
      >
        <header className="border-b border-outline-variant/30 px-6 py-4 dark:border-outline/30">
          <div className="flex items-center gap-3">
            <h2 className="min-w-0 flex-1 truncate font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
              {title}
            </h2>
            {headerAccessory}
            <button type="button" className="btn-ghost shrink-0 !px-2" onClick={onClose} aria-label="Schließen">
              <Icon name="close" />
            </button>
          </div>
          {subtitle != null && (
            <p className="mt-1 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">{subtitle}</p>
          )}
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer != null && (
          <footer className="flex justify-end gap-3 border-t border-outline-variant/30 px-6 py-4 dark:border-outline/30">
            {footer}
          </footer>
        )}
      </aside>
    </div>
  );
}

/** Zentrierter Dialog (Stitch: "Angebot senden"-Compose) — im Unterschied zum Drawer NICHT
 *  seitlich einfahrend, sondern eine mittig platzierte Karte über einem abgedunkelten Hintergrund. */
export function Modal({
  open,
  onClose,
  width = "600px",
  children,
}: {
  open: boolean;
  onClose: () => void;
  width?: string;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-6">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} aria-hidden />
      <div
        className="relative flex max-h-full flex-col overflow-hidden rounded-2xl bg-surface-container-lowest shadow-2xl dark:bg-night-card"
        style={{ width }}
      >
        {children}
      </div>
    </div>
  );
}

/** I1: Icon-Kreis (accent 10%) + optionaler Primär-CTA + optionaler Tipp (Design-Vorlage 3i). */
export function EmptyState({
  icon,
  title,
  hint,
  cta,
  tip,
}: {
  icon: string;
  title: string;
  hint?: string;
  cta?: { label: string; onClick: () => void; icon?: string };
  tip?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3.5 py-14 text-center">
      <div className="grid h-[66px] w-[66px] shrink-0 place-items-center rounded-full bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-fixed-dim">
        <Icon name={icon} className="text-[30px]" />
      </div>
      <div>
        <p className="text-[17px] font-bold tracking-[-0.01em] text-on-background dark:text-inverse-on-surface">{title}</p>
        {hint && (
          <p className="mx-auto mt-1.5 max-w-sm font-body-sm text-[12.5px] leading-relaxed text-secondary dark:text-secondary-fixed-dim">
            {hint}
          </p>
        )}
      </div>
      {cta && (
        <button type="button" className="btn-primary" onClick={cta.onClick}>
          {cta.icon && <Icon name={cta.icon} className="text-[16px]" />} {cta.label}
        </button>
      )}
      {tip && (
        <div className="flex items-center gap-1.5 rounded-lg border border-surface-variant bg-surface-container-low px-3 py-2 font-body-sm text-[11.5px] text-secondary-fixed-dim dark:border-outline/30 dark:bg-surface-variant/10">
          <Icon name="info" className="text-[14px]" /> {tip}
        </div>
      )}
    </div>
  );
}

/** I3: Karten-Skeleton (Avatar + 2 Zeilen + Pill) statt schlichter Pulse-Balken. */
export function LoadingRows({ rows = 4 }: { rows?: number }) {
  return (
    <div className="divide-y divide-surface-variant/60 dark:divide-outline/20" aria-busy>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex animate-pulse items-center gap-3.5 px-4 py-4">
          <div className="h-9 w-9 shrink-0 rounded-full bg-surface-variant dark:bg-white/[0.08]" />
          <div className="flex-1 space-y-2">
            <div className="h-2.5 w-[45%] rounded bg-surface-variant dark:bg-white/[0.08]" />
            <div className="h-2 w-[68%] rounded bg-surface-container-high dark:bg-white/[0.05]" />
          </div>
          <div className="h-5 w-16 shrink-0 rounded-full bg-surface-container-high dark:bg-white/[0.05]" />
        </div>
      ))}
    </div>
  );
}

/** I2: Icon-Kachel + Titel + optionaler Retry-Button + optional Fehlercode/Zeit (Design-Vorlage 3i). */
export function ErrorNote({
  message,
  title = "Etwas ist schiefgelaufen",
  meta,
  onRetry,
}: {
  message: string;
  title?: string;
  meta?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="m-4 flex items-start gap-3 rounded-xl border border-[#F1D2CB] bg-[#FBEDEA] p-4 dark:border-[#B23B32]/30 dark:bg-[#B23B32]/10">
      <div className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[9px] bg-[#F5DAD3] text-[#B23B32] dark:bg-[#B23B32]/25 dark:text-[#e39b92]">
        <Icon name="error" className="text-[18px]" />
      </div>
      <div className="flex-1">
        <p className="font-body-sm text-[14px] font-bold text-[#8F2E26] dark:text-[#e39b92]">{title}</p>
        <p className="mt-0.5 font-body-sm text-[12px] leading-relaxed text-[#9A5049] dark:text-[#e39b92]/80">{message}</p>
        {(onRetry || meta) && (
          <div className="mt-3 flex items-center gap-2.5">
            {onRetry && (
              <button type="button" className="btn-primary !py-2 !text-[12px]" onClick={onRetry}>
                <Icon name="refresh" className="text-[14px]" /> Erneut versuchen
              </button>
            )}
            {meta && <span className="font-body-sm text-[11.5px] text-[#9A5049]/90">{meta}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
      <div>
        <h1 className="font-display-lg text-display-lg text-on-background dark:text-inverse-on-surface">{title}</h1>
        {subtitle != null && (
          <div className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{subtitle}</div>
        )}
      </div>
      {actions != null && <div className="flex items-center gap-3">{actions}</div>}
    </div>
  );
}
